/**
 * SlideOverPanel — a panel that slides in from the right over the page, for
 * detail that belongs beside what you are looking at (a location's metrics).
 * Its width follows the screen: the whole width on a phone, most of a tablet,
 * a side panel on a desktop — the page stays visible (dimmed by `overlay`)
 * behind it wherever there is room.
 *
 * Design system: the panel is the page ground `bg` with a `borderSubtle` edge;
 * its head is a `bgSubtle` band with the title in `sectionTitle` and the
 * subtitle in `meta`; the close button is a round 44px secondary Button. A
 * `pinned` area (the main action) stays under the head while the body scrolls.
 *
 * A modal dialog: Escape or the backdrop closes it, focus starts on its close
 * button, Tab stays inside, focus goes back to the opener on close, and the
 * page behind does not scroll.
 *
 * The body stacks its children at their own height and scrolls (a grid with
 * max-content rows, so nothing is squashed to fit). A child therefore can't
 * stretch to fill the rest of the height: put footer-like actions in `pinned`.
 */
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { textRole, tokenVars } from '@turn-connect/ui';
import { Button } from './Button';

export interface SlideOverPanelProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  /** Re-focuses the close button when it changes (a different item opened). */
  focusKey?: string;
  /** Pinned under the header while the body scrolls (e.g. a primary action). */
  pinned?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
}

/** Widths, motion and focus (inline styles cannot hold media queries or keyframes). */
const PANEL_CSS = `
.tc-slide-over-backdrop { position: fixed; inset: 0; z-index: 999; background: ${tokenVars.overlay}; animation: tc-slide-over-fade 160ms ease-out; }
.tc-slide-over { position: fixed; top: 0; right: 0; bottom: 0; z-index: 1000; box-sizing: border-box; width: 100vw; display: flex; flex-direction: column;
  background: ${tokenVars.bg}; color: ${tokenVars.text.primary}; border-left: 1px solid ${tokenVars.borderSubtle};
  box-shadow: -8px 0 24px ${tokenVars.overlay}; animation: tc-slide-over-in 200ms ease-out; }
@media (min-width: 640px) { .tc-slide-over { width: min(80vw, 640px); } }
@media (min-width: 1100px) { .tc-slide-over { width: 480px; } }
.tc-slide-over button:focus-visible { outline: 2px solid ${tokenVars.focus}; outline-offset: 2px; }
@keyframes tc-slide-over-in { from { transform: translateX(32px); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes tc-slide-over-fade { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .tc-slide-over, .tc-slide-over-backdrop { animation: none; } }
`;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const SlideOverPanel: React.FC<SlideOverPanelProps> = ({ title, subtitle, onClose, focusKey, pinned, children, testId }) => {
  const close = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    close.current?.focus();
  }, [focusKey]);

  // Give focus back to whatever opened the panel when it closes.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    return () => opener?.focus?.();
  }, []);

  // Escape closes; the latest onClose without re-binding on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      // Tab stays inside the panel (it is modal).
      if (e.key !== 'Tab' || !panel.current) return;
      const focusable = panel.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const inside = panel.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, []);

  return (
    <>
      <style>{PANEL_CSS}</style>
      <div
        className="tc-slide-over-backdrop"
        onClick={onClose}
        aria-hidden="true"
        data-testid={testId ? `${testId}-backdrop` : undefined}
      />
      <div ref={panel} className="tc-slide-over" role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '12px 16px',
            background: tokenVars.bgSubtle,
            borderBottom: `1px solid ${tokenVars.borderSubtle}`,
          }}
        >
          <div style={{ flexGrow: 1, minWidth: 0, display: 'grid', gap: 2, alignSelf: 'center' }}>
            <h2
              style={{ ...textRole('sectionTitle'), margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              data-testid={testId ? `${testId}-title` : undefined}
            >
              {title}
            </h2>
            {subtitle && <div style={textRole('meta')}>{subtitle}</div>}
          </div>
          <Button
            ref={close}
            onClick={onClose}
            aria-label="Close"
            data-testid={testId ? `${testId}-close` : undefined}
            style={{ width: 44, height: 44, minHeight: 44, padding: 0, borderRadius: 22, flexShrink: 0 }}
          >
            <X size={18} aria-hidden="true" />
          </Button>
        </div>
        {pinned && <div style={{ padding: '12px 16px', borderBottom: `1px solid ${tokenVars.borderSubtle}` }}>{pinned}</div>}
        {/* Rows as tall as their content, so the body scrolls: in a flex column
            (or auto grid rows) a block that clips (SectionBox, overflow
            hidden) has no minimum height and gets squashed to fit instead. */}
        <div
          style={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: 16,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridAutoRows: 'max-content',
            alignContent: 'start',
            gap: 12,
          }}
          data-testid={testId ? `${testId}-body` : undefined}
          data-scroll-region
        >
          {children}
        </div>
      </div>
    </>
  );
};

export default SlideOverPanel;
